"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Autoplay, Keyboard } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import type { HeroContentWithStoreData } from "@/types/heroContent";
import styles from "./HeroSlider.module.css";
import { getStoreLogoAlt } from "@/lib/storeLogo";

import "swiper/css";

interface HeroSliderProps {
  heroContents: HeroContentWithStoreData[];
}

function getFullImageUrl(path?: string) {
  if (!path) return "";
  if (/^https:\/\//i.test(path)) return path;
  if (/^http:\/\//i.test(path)) return path.replace(/^http:\/\//i, "https://");
  return `https://www.itscoupons.com${path.startsWith("/") ? path : `/${path}`}`;
}

function HeroPromotionCard({
  heroContent,
  priority = false,
}: {
  heroContent: HeroContentWithStoreData;
  priority?: boolean;
}) {
  const backgroundColor = heroContent.backgroundColor || "#ffffff";
  const ctaHref = heroContent.buttonUrl?.trim() ||
    (heroContent.store?.slug ? `/stores/${heroContent.store.slug}` : "");

  return (
    <article className="relative px-1 pb-10 pt-1 sm:px-2">
      <div className="relative">
        <div className="relative grid min-h-[350px] grid-cols-1 overflow-visible rounded-[28px] shadow-[0_18px_50px_rgba(20,25,35,0.14)] md:min-h-[300px] md:grid-cols-[7fr_5fr] lg:min-h-[320px]">
          <div
            className="relative aspect-[16/9] min-h-[170px] overflow-hidden rounded-t-[28px] bg-gray-100 md:aspect-auto md:min-h-[300px] md:rounded-bl-[28px] md:rounded-br-none md:rounded-tl-[28px] md:rounded-tr-none lg:min-h-[320px]"
            style={{ backgroundColor }}
          >
            {heroContent.backgroundImage ? (
              <Image
                src={getFullImageUrl(heroContent.backgroundImage)}
                alt={`${heroContent.title} promotion`}
                width={1600}
                height={900}
                priority={priority}
                loading={priority ? undefined : "lazy"}
                sizes="(max-width: 639px) 88vw, (max-width: 1023px) 82vw, 40vw"
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full min-h-[170px] items-center justify-center p-6 text-center md:min-h-[300px] lg:min-h-[320px]">
                <span className="text-xl font-semibold" style={{ color: heroContent.titleColor || "#1f2937" }}>
                  {heroContent.store?.name || heroContent.title}
                </span>
              </div>
            )}
          </div>

          <div
            className="flex min-h-[180px] flex-col items-center justify-center rounded-b-[28px] px-5 py-5 text-center md:min-h-[300px] md:items-start md:rounded-bl-none md:rounded-br-[28px] md:rounded-tr-[28px] md:px-7 md:text-left lg:min-h-[320px] lg:px-9"
            style={{ backgroundColor }}
          >
            <div className="mb-2 hidden items-center gap-2 text-xs font-semibold text-gray-600 sm:text-sm md:flex">
              {heroContent.store?.name && <span>{heroContent.store.name}</span>}
            </div>
            <h2
              className="line-clamp-3 text-xl font-bold leading-tight sm:text-2xl lg:text-3xl"
              style={{ color: heroContent.titleColor || "#111827" }}
            >
              {heroContent.title}
            </h2>
            {heroContent.subtitle && (
              <p
                className="mt-2 line-clamp-2 max-w-xl text-sm leading-relaxed sm:text-base"
                style={{ color: heroContent.subtitleColor || "#4b5563" }}
              >
                {heroContent.subtitle}
              </p>
            )}
            {ctaHref && (
              <Link
                href={ctaHref}
                className="mt-4 inline-flex min-h-10 items-center justify-center rounded-full px-5 text-sm font-semibold shadow-sm transition hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-700"
                style={{
                  backgroundColor: heroContent.buttonColor || "#7c3aed",
                  color: heroContent.buttonTextColor || "#ffffff",
                }}
              >
                {heroContent.buttonText || "Shop Now"}
              </Link>
            )}
          </div>
        </div>

        {heroContent.store?.image && heroContent.store.slug && (
          <Link
            href={`/stores/${heroContent.store.slug}`}
            aria-label={`Visit ${heroContent.store.name} store page`}
            className="absolute left-1/2 top-[50%] z-20 flex h-[68px] w-[68px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 border-white bg-white shadow-xl transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-700 md:top-full md:h-[76px] md:w-[76px]"
          >
            <span className="relative h-full w-full overflow-hidden rounded-full">
              <Image
                src={getFullImageUrl(heroContent.store.image)}
                alt={getStoreLogoAlt(heroContent.store.slug)}
                width={76}
                height={76}
                priority={priority}
                sizes="76px"
                className="h-full w-full object-cover"
              />
            </span>
          </Link>
        )}
      </div>
    </article>
  );
}

export default function HeroSlider({ heroContents }: HeroSliderProps) {
  const [isClient, setIsClient] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperInstance | null>(null);
  const wrapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setIsClient(true);
    return () => {
      if (wrapTimerRef.current) clearTimeout(wrapTimerRef.current);
    };
  }, []);

  if (!heroContents.length) return null;

  const hasMultipleSlides = heroContents.length > 1;
  const carouselSlides = hasMultipleSlides
    ? [
        { heroContent: heroContents[heroContents.length - 1], key: `previous-${heroContents[heroContents.length - 1]._id}`, isClone: true },
        ...heroContents.map((heroContent) => ({ heroContent, key: heroContent._id, isClone: false })),
        { heroContent: heroContents[0], key: `next-${heroContents[0]._id}`, isClone: true },
      ]
    : heroContents.map((heroContent) => ({ heroContent, key: heroContent._id, isClone: false }));

  const goToSlide = (index: number) => {
    swiperRef.current?.slideTo(hasMultipleSlides ? index + 1 : index);
  };

  const getOriginalIndex = (swiper: SwiperInstance) =>
    hasMultipleSlides
      ? (swiper.activeIndex - 1 + heroContents.length) % heroContents.length
      : swiper.activeIndex;

  return (
    <section
      aria-label="Featured promotions"
      className={`relative mt-1 w-full pt-1 pb-2 sm:mt-3 sm:pt-1 sm:pb-4 ${styles.viewport}`}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") swiperRef.current?.slidePrev();
        if (event.key === "ArrowRight") swiperRef.current?.slideNext();
      }}
      tabIndex={0}
    >
      <h1 className="sr-only">Best Coupons, Promo Codes & Deals - ITS Coupons</h1>

      {hasMultipleSlides && (
        <>
          <button
            type="button"
            aria-label="Previous promotion"
            onClick={() => swiperRef.current?.slidePrev()}
            className={`absolute top-[43%] z-30 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-800 shadow-lg transition hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-700 md:flex ${styles.previous}`}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Next promotion"
            onClick={() => swiperRef.current?.slideNext()}
            className={`absolute top-[43%] z-30 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-800 shadow-lg transition hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-700 md:flex ${styles.next}`}
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </>
      )}

      {isClient ? (
        <Swiper
          modules={[Autoplay, Keyboard]}
          slidesPerView="auto"
          centeredSlides
          centeredSlidesBounds={false}
          spaceBetween={20}
          initialSlide={hasMultipleSlides ? 1 : 0}
          autoplay={hasMultipleSlides ? { delay: 5500, disableOnInteraction: false, pauseOnMouseEnter: true } : false}
          keyboard={{ enabled: true, onlyInViewport: true }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            setActiveIndex(getOriginalIndex(swiper));
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(getOriginalIndex(swiper));
            if (wrapTimerRef.current) clearTimeout(wrapTimerRef.current);

            const isPreviousClone = hasMultipleSlides && swiper.activeIndex === 0;
            const isNextClone = hasMultipleSlides && swiper.activeIndex === heroContents.length + 1;
            if (isPreviousClone || isNextClone) {
              const cloneIndex = swiper.activeIndex;
              const realIndex = isPreviousClone ? heroContents.length : 1;
              wrapTimerRef.current = setTimeout(() => {
                wrapTimerRef.current = null;
                if (!swiper.destroyed && swiper.activeIndex === cloneIndex) {
                  swiper.slideTo(realIndex, 0, false);
                }
              }, (swiper.params.speed ?? 300) + 40);
            }
          }}
          className={styles.swiper}
        >
          {carouselSlides.map(({ heroContent, key, isClone }, index) => (
            <SwiperSlide key={key} className={styles.slide} data-clone={isClone ? "true" : undefined}>
              <HeroPromotionCard heroContent={heroContent} priority={index === (hasMultipleSlides ? 1 : 0)} />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div className={styles.fallbackSlide}>
          <HeroPromotionCard heroContent={heroContents[0]} priority />
        </div>
      )}

      {heroContents.length > 0 && (
        <div className="mt-0 flex min-h-6 items-center justify-center gap-2" aria-label="Promotion pagination">
          {heroContents.map((heroContent, index) => (
            <button
              key={heroContent._id}
              type="button"
              aria-label={`Go to promotion ${index + 1}`}
              aria-pressed={activeIndex === index}
              onClick={() => goToSlide(index)}
              className={`relative h-3.5 w-3.5 min-h-[18px] min-w-[18px] rounded-full transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-700 ${activeIndex === index ? "w-6 bg-purple-700" : "bg-gray-300 hover:bg-gray-500"}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
